import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { useTranslation } from "react-i18next";
import { AnimatedCounter } from "@/components/ui-iyaf/AnimatedCounter";
import { countries, partners, wholeImpactMetrics } from "@/data/content";

// Public world atlas topojson — loaded by the browser at runtime.
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function Impact() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <section className="relative pt-16 pb-12" data-testid="impact-hero">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-5">{t("impact.eyebrow")}</p>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] max-w-4xl text-balance text-5xl sm:text-6xl lg:text-7xl">
            {t("impact.title")}
          </h1>
          <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-3xl">
            {t("impact.lead")}
          </p>
        </div>
      </section>

      {/* Real world map with country pins */}
      <section className="pb-20">
        <div className="container-iyaf">
          <div className="relative rounded-3xl border border-black/5 bg-white overflow-hidden p-4 sm:p-6" data-testid="impact-map">
            <div className="relative w-full aspect-[2/1] rounded-2xl bg-iyaf-paper overflow-hidden">
              <ComposableMap
                projection="geoEqualEarth"
                projectionConfig={{ scale: 175 }}
                style={{ width: "100%", height: "100%" }}
              >
                <ZoomableGroup center={[20, 20]} zoom={1} maxZoom={1} minZoom={1}>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map((geo) => (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill="rgba(54,99,168,0.12)"
                          stroke="rgba(54,99,168,0.30)"
                          strokeWidth={0.4}
                          style={{
                            default: { outline: "none" },
                            hover: { outline: "none", fill: "rgba(54,99,168,0.22)" },
                            pressed: { outline: "none" },
                          }}
                        />
                      ))
                    }
                  </Geographies>

                  {countries.map((c, i) => (
                    <Marker key={c.name} coordinates={[c.coord[1], c.coord[0]]} data-testid={`map-pin-${c.name.toLowerCase()}`}>
                      <motion.g
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 + i * 0.08, type: "spring", stiffness: 180 }}
                      >
                        {c.featured ? (
                          <>
                            <circle r={10} fill="#FF9F1C" fillOpacity="0.25">
                              <animate attributeName="r" from="6" to="14" dur="1.6s" repeatCount="indefinite" />
                              <animate attributeName="opacity" from="0.45" to="0" dur="1.6s" repeatCount="indefinite" />
                            </circle>
                            <circle r={5} fill="#FF9F1C" stroke="#fff" strokeWidth={1.5} />
                          </>
                        ) : (
                          <>
                            <circle r={4} fill="#3663A8" stroke="#fff" strokeWidth={1.2} />
                          </>
                        )}
                        <text
                          x={0}
                          y={-12}
                          textAnchor="middle"
                          style={{
                            fontFamily: "Nunito, system-ui, sans-serif",
                            fontWeight: 700,
                            fontSize: 9,
                            fill: "#111827",
                            paintOrder: "stroke",
                            stroke: "#FFFFFF",
                            strokeWidth: 3,
                            strokeLinejoin: "round",
                          }}
                        >
                          {c.name}
                        </text>
                      </motion.g>
                    </Marker>
                  ))}
                </ZoomableGroup>
              </ComposableMap>
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {countries.map((c) => (
                <div key={c.name} className={`p-4 rounded-2xl border ${c.featured ? "border-iyaf-orange/40 bg-iyaf-orange/5" : "border-black/5"}`}>
                  <p className="font-display font-black text-base">{t(`impactDeep.countries.${c.name}.name`, c.name)}</p>
                  <p className="text-xs text-iyaf-ink/55 mt-1">{t(`impactDeep.countries.${c.name}.role`, c.role)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Metrics dashboard */}
      <section className="py-20 bg-iyaf-ink text-white">
        <div className="container-iyaf">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="eyebrow text-iyaf-orange mb-3">{t("impactDeep.byNumbersEyebrow")}</p>
              <h2 className="font-display text-4xl sm:text-5xl font-black">{t("impactDeep.byNumbersTitle")}</h2>
            </div>
            <p className="text-white/55 max-w-md text-sm">{t("impactDeep.byNumbersNote")}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 rounded-3xl overflow-hidden">
            {wholeImpactMetrics.map((m, i) => (
              <motion.div
                key={m.k}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: (i % 5) * 0.04 }}
                className="bg-iyaf-ink p-5 sm:p-6"
                data-testid={`metric-${m.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <p className="font-display text-3xl sm:text-4xl font-black"><AnimatedCounter value={m.v} /></p>
                <p className="text-[10px] sm:text-xs uppercase tracking-widest text-white/55 mt-2">{t(`impactDeep.metrics.${m.k}`, m.k)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners with real logos */}
      <section className="py-24">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-4">{t("impactDeep.trustedBy")}</p>
          <h2 className="h2 mb-12 text-balance">{t("impactDeep.partnersTitle")}</h2>

          <p className="eyebrow text-iyaf-blue mb-5">{t("impactDeep.partnersStrategic")}</p>
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {partners.strategic.map((p) => <PartnerCard key={p.name} p={p} />)}
          </div>

          <p className="eyebrow text-iyaf-orange mb-5">{t("impactDeep.partnersIntl")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {partners.international.map((p) => <PartnerCard key={p.name} p={p} />)}
          </div>
        </div>
      </section>
    </div>
  );
}

function PartnerCard({ p }) {
  return (
    <a
      href={p.site}
      target="_blank"
      rel="noreferrer"
      className="group p-6 sm:p-7 rounded-3xl bg-white border border-black/5 hover:border-iyaf-blue/30 transition flex flex-col items-center text-center min-h-[160px] justify-center"
      data-testid={`partner-${p.short.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <img
        src={p.logo}
        alt={p.name}
        loading="lazy"
        className="h-14 sm:h-16 w-auto object-contain mb-3 grayscale opacity-75 group-hover:grayscale-0 group-hover:opacity-100 transition"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <p className="font-display font-black text-base leading-tight">{p.name}</p>
      {p.short !== p.name && <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/45 mt-1">{p.short}</p>}
    </a>
  );
}
